import { useFocusEffect } from '@react-navigation/native';
import { useGlobalConfig } from 'context/GlobalContext';
import { useSocket } from 'context/SocketContext';
import { useMobileOrDesktop } from 'hoc/mobileOrDesktop';
import { useSelectedProfile } from 'hooks/useSelectedProfile';
import { RootStackScreenProps } from 'navigation/AppNavigator';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useAnalytics } from 'services/analytics/useAnalytics';
import { ProgramType } from 'utils/enum';
import { calcSizeForDevice } from 'utils/index';
import { Platform } from 'utils/platform';
import { modifyVastUrl } from 'utils/vast';

import { heroStyle } from './ProgramScreen';

import Hero from 'components/Hero';
import Player from 'components/Player';
import SpallaPlayerPlatform from 'components/Player/SpallaPlayer/SpallaPlayerPlatform';
import { usePlayer } from 'components/Player/context';
import ProgramContent from 'components/Program/Content';
import ProgramRadio from 'components/Program/Radio';
import ProgramRelated from 'components/Program/Related';
import { useProgram } from 'components/Program/context';

const RadioScreen: React.FC<RootStackScreenProps<'Program'>> = ({ navigation }) => {
  const program = useProgram();
  const analytics = useAnalytics();
  const [mediaId, setMediaId] = useState<string>();
  const [isActive, setIsActive] = useState(false);
  const [openVideo, setOpenVideo] = useState(false);
  const { isPlaying, isFullScreen, isStreaming, pause, isVisible, mute, setNextTrack, reset } =
    usePlayer();
  const { ads } = useGlobalConfig();
  const { selectedProfile } = useSelectedProfile();
  const { watch } = useSocket();
  const isUnder18 = selectedProfile?.ageRating !== '18';

  useEffect(() => {
    setNextTrack(undefined);
  }, []);

  useEffect(() => {
    return navigation.addListener('beforeRemove', async () => {
      if (isPlaying && !isStreaming) pause();
      mute(false);
    });
  }, [navigation, isPlaying, isStreaming, pause]);

  useEffect(() => {
    if (Platform.isAndroidTV && !isVisible) {
      setOpenVideo(false);
    }
  }, [isVisible]);

  useFocusEffect(
    useCallback(() => {
      setIsActive(true);

      return () => {
        if (!isStreaming) pause();
        setIsActive(false);
      };
    }, [isStreaming])
  );

  useEffect(() => {
    return navigation.addListener('beforeRemove', () => {
      if (Platform.OS === 'ios') {
        reset({ media: undefined });
      }
    });
  }, []);

  useFocusEffect(
    useCallback(() => {
      analytics.triggerRadioScreenPageViewEvent({
        programId: program._id,
        programTitle: program.title,
        programAgeRating: program.rating,
        programAdUnit: program.ads?.adunit?.val!,
        programCategory: program.category.map((tag) => tag.name).join(', '),
        programDirector: program.director,
        programGenre: program.genre.map((genre) => genre.name).join(', '),
        programYear: program.year,
      });
    }, [])
  );

  const playerProps: PlayerProps | undefined = useMemo(() => {
    if (!program) {
      return undefined;
    }

    const media: PlayerMedia = {
      title: program.title,
      image: [program.imageVertical?.url!, program.imageHorizontal?.url!],
      duration: program.duration,
      programId: program._id,
      programTitle: program.title,
      programUrl: program.url,
      ratingValue: program.rating,
      ratingDescription: program.rateDescription,
      videoTracks: [],
      mediaSource: program.spallaID || program.mediaSource ? 'spalla' : undefined,
    };

    const mediaIdDub = program.mediaIdDub;
    const mediaIdDubSpalla = program.videoIdDub;

    if (mediaIdDub) {
      media.videoTracks.push({
        language: 'dub',
        trackId: media.mediaSource ? mediaIdDubSpalla : mediaIdDub,
      });
    }

    media.videoTracks.push({
      language: 'origin',
      trackId: media.mediaSource ? program.spallaID : program.mediaId,
    });

    return {
      media,
      isLive: program.type == ProgramType.LIVE || program.type == ProgramType.RADIO,
      adTagUrl:
        ads?.disableAds || program.ads?.disableAds || isUnder18
          ? undefined
          : Platform.isWebTV
          ? ads?.ctvVast
          : modifyVastUrl(ads?.vast, program),
      adsExpirationTime: ads?.adsExpirationTime,
    };
  }, [program, mediaId]);

  useEffect(() => {
    if (isActive && playerProps?.media) {
      watch(playerProps).catch(() => undefined);
    }
  }, [isActive, playerProps?.media?.mediaId, playerProps?.media?.programId]);

  const Video = useMemo(
    () =>
      mediaId != undefined && isActive && playerProps != undefined ? (
        <Player {...playerProps} autoplay />
      ) : undefined,
    [mediaId, isActive, playerProps]
  );

  const [VideoMobile, VideoDesktop] = useMobileOrDesktop(Video, Video);

  return (
    <>
      {(Platform.isAndroidTV || Platform.OS === 'web' || !isFullScreen) && (
        <Hero program={program} hideCover={mediaId !== undefined}>
          {VideoMobile !== undefined && (
            <SpallaPlayerPlatform streamId={program?.spallaID} autoplay={true} isLive />
          )}

          <View style={heroStyle.container}>
            <Hero.Header
              program={program}
              Aside={
                VideoDesktop != undefined && !Platform.isAndroidTV ? (
                  <View style={styles.videoBox}>
                    {Platform.isWebTV ? (
                      <>{Video}</>
                    ) : (
                      <SpallaPlayerPlatform streamId={program.spallaID} autoplay={true} isLive />
                    )}
                  </View>
                ) : undefined
              }
              isVideoOpen={openVideo}>
              <ProgramRadio
                mediaId={mediaId}
                setMediaId={setMediaId}
                openVideo={openVideo}
                setOpenVideo={setOpenVideo}
              />
              <ProgramContent />
            </Hero.Header>

            <View style={styles.relatedContainer}>
              <ProgramRelated />
            </View>
          </View>
        </Hero>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  videoBox: {
    aspectRatio: 1 / 0.56,
    width: Platform.isTV ? 0 : '80%',
    overflow: 'hidden',
  },
  relatedContainer: {
    paddingVertical: calcSizeForDevice(24),
  },
});

export default RadioScreen;
